import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Ref } from 'vue'
import type { DocumentData, DocumentReference } from 'firebase/firestore'
import { getDocs, query, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore'
import type { StorageReference } from 'firebase/storage'
import { getDownloadURL, listAll, ref as storageRef, uploadBytes } from 'firebase/storage'
import { errorDefault, savedDefault } from '../helpers/snackbar'
import { collectionOwners } from '../firestoreWrappers/firestoreWrappers'
import { firestore, storage } from '../firebaseCore'
import type { CompanyModel } from '../models/CompanyModel'
import { mapCompany } from '../models/CompanyModel'
import { mapOwners } from '../models/ManagerModel'
import { collectionCompanies } from '../firebase/firestoreCompanies'
import { AgreementModel } from '~/models/AgreementModel'
import type { OwnerModel } from '~/models/OwnerModel'
import { useSharedStore } from './shared'
import { useSnackbarStore } from './snackbar'

const mapPromises = (item: StorageReference) => getDownloadURL(item)
function mapFunc(data: any[]) {
  return (item: any, index: number) => ({
    name: item.name,
    url: data[index],
  })
}

function mapByOwner(owner: OwnerModel) {
  return (ownerData: OwnerModel) => {
    if (ownerData?.reference?.id === owner?.reference?.id)
      // @ts-expect-error
      ownerData.loading = true
    return ownerData
  }
}

function mapByOwner2(owner: OwnerModel) {
  return (ownerData: OwnerModel) => {
    if (ownerData?.reference?.id === owner?.reference?.id) {
      ownerData.isOwner = !owner?.isOwner
      // @ts-expect-error
      delete ownerData.loading
    }
    return ownerData
  }
}

function mapByOwner3(owner: OwnerModel) {
  return (ownerData: OwnerModel) => {
    if (ownerData?.reference?.id === owner?.reference?.id)
      // @ts-expect-error
      delete ownerData.loading
    return ownerData
  }
}

function mapByCompany(company: CompanyModel, agreement: AgreementModel) {
  return (companyData: CompanyModel) => {
    if (companyData?.reference?.id === company?.reference?.id)
      companyData.agreement = agreement
    return companyData
  }
}

export const useCompaniesStore = defineStore('companies', () => {
  const companies: Ref<CompanyModel[] | null> = ref(null)
  const agreements: Ref<AgreementModel[] | null> = ref(null)
  const loadingAgreements: Ref<boolean> = ref(false)
  const errorAgreements: Ref<unknown | null> = ref(null)
  const owners: Ref<OwnerModel[] | null> = ref(null)
  const loadingOwners: Ref<boolean> = ref(false)
  const errorOwners: Ref<unknown | null> = ref(null)

  const resetState = () => {
    companies.value = null
    agreements.value = null
    loadingAgreements.value = false
    errorAgreements.value = null
    owners.value = null
    loadingOwners.value = false
    errorOwners.value = null
  }

  const getCompanies = () => {
    useSharedStore().init()

    const onSuccess = ([{ docs }]: DocumentData[]) => {
      companies.value = docs.map(mapCompany)
      useSharedStore().success()
    }

    const onError = (error: unknown) => {
      console.error(error)
      useSharedStore().errorAction(error)
      useSnackbarStore().showSnackbar(errorDefault(error))
    }

    return Promise.all([getDocs(collectionCompanies)])
      .then(onSuccess)
      .catch(onError)
  }

  const getOwners = (companyRef: DocumentReference) => {
    loadingOwners.value = true
    errorOwners.value = null

    const onSuccess = (arg0: DocumentData) => {
      loadingOwners.value = false
      owners.value = arg0.docs.map(mapOwners)
    }

    const onError = (error: unknown) => {
      console.error(error)
      loadingOwners.value = false
      errorOwners.value = error
      useSnackbarStore().showSnackbar(errorDefault(error))
    }

    return getDocs(query(collectionOwners, where('company', '==', companyRef)))
      .then(onSuccess)
      .catch(onError)
  }

  const getAgreements = (companyRef: DocumentReference) => {
    loadingAgreements.value = true
    errorAgreements.value = null

    const onError = (error: unknown) => {
      console.error(error)
      loadingAgreements.value = false
      errorAgreements.value = error
      useSnackbarStore().showSnackbar(errorDefault(error))
    }

    const onSuccess = ({ items }: { items: DocumentData }) => {
      Promise.all(items.map(mapPromises))
        .then((data) => {
          loadingAgreements.value = false
          agreements.value = items.map(mapFunc(data))
        })
        .catch(onError)
    }

    const path = `${companyRef.id}/docs/`

    return listAll(storageRef(storage, path))
      .then(onSuccess)
      .catch(onError)
  }

  const updateOwnerStatus = (owner: OwnerModel, userData: any) => {
    const newOwnerData = {
      isOwner: !owner?.isOwner,
      lastUpdateByUser: userData.reference,
      lastUpdateTime: serverTimestamp(),
    }

    const byId = ({ reference }: { reference: DocumentReference }) => reference.id === owner.company?.id
    // @ts-expect-error todo
    const [ownerCompany] = companies.value.filter(byId)
    const acceptedByObject = ownerCompany?.agreement?.acceptedBy || {}

    if (!owner?.isOwner)
    // @ts-expect-error
      acceptedByObject[owner.reference.id] = false
    else
    // @ts-expect-error
      delete acceptedByObject[owner.reference.id]

    const newCompanyAgreement = {
      agreement: {
        ...ownerCompany?.agreement || {},
        acceptedBy: acceptedByObject,
      },
    }

    owners.value = owners.value?.map(mapByOwner(owner)) || null

    const onError = (error: unknown) => {
      console.error(error)
      owners.value = owners.value?.map(mapByOwner3(owner)) || null
      useSnackbarStore().showSnackbar(errorDefault(error))
    }

    const onSuccess = () => {
      owners.value = owners.value?.map(mapByOwner2(owner)) || null
      // @ts-expect-error
      companies.value = companies.value?.map(mapByCompany(ownerCompany, newCompanyAgreement.agreement)) || null
    }

    const newCompanyAgreementData = {
      ...newCompanyAgreement,
      lastUpdateByUser: userData.reference,
      lastUpdateTime: serverTimestamp(),
    }

    const batch = writeBatch(firestore)

    return batch
    // @ts-expect-error
      .update(owner.reference, newOwnerData)
      // @ts-expect-error todo
      .update(ownerCompany.reference, newCompanyAgreementData)
      .commit()
      .then(onSuccess)
      .catch(onError)
  }

  const uploadAgreement = (selectedFile: any, company: CompanyModel) => {
    if (!company?.reference) {
      return Promise.reject(new Error('Company reference is null'))
    }

    const activeFile = `${new Date().toISOString()}${selectedFile.name}`
    const path = `${company.reference.id}/docs/${activeFile}`

    const onSuccess = (result: any) => {
      getDownloadURL(result.ref).then((url) => {
        const agreement = {
          name: result.metadata.name,
          url,
        }
        // @ts-expect-error todo
        agreements.value = [...agreements.value || [], agreement]
      })
    }

    const onError = (error: unknown) => {
      console.error(error)
      useSnackbarStore().showSnackbar(errorDefault(error))
    }

    return uploadBytes(storageRef(storage, path), selectedFile)
      .then(onSuccess)
      .catch(onError)
  }

  const updateCompanyAgreement = (agreement: AgreementModel, company: CompanyModel, userData: any) => {
    const onSuccess = () => {
      companies.value = companies.value?.map(mapByCompany(company, new AgreementModel({
        ...company?.agreement || {},
        ...agreement,
      }))) || null
      useSnackbarStore().showSnackbar(savedDefault)
    }

    const onError = (error: unknown) => {
      console.error(error)
      useSnackbarStore().showSnackbar(errorDefault(error))
    }
    if (!company?.reference) {
      return Promise.reject(new Error('Company reference is null'))
    }

    return updateDoc(company.reference, {
      agreement: {
        ...company?.agreement || {},
        ...agreement,
      },
      lastUpdateByUser: userData.reference,
      lastUpdateTime: serverTimestamp(),
    })
      .then(onSuccess)
      .catch(onError)
  }

  return {
    companies,
    agreements,
    loadingAgreements,
    errorAgreements,
    owners,
    loadingOwners,
    errorOwners,
    resetState,
    getCompanies,
    getOwners,
    getAgreements,
    updateOwnerStatus,
    uploadAgreement,
    updateCompanyAgreement,
  }
})
